<script setup lang="ts">
/**
 * 03-app-shell.md -> "Shell layout". Fixed 248px rail (Rail.vue), full viewport height; a scrolling
 * <main> on `paper` fills the rest. The rail never scrolls with the page — only its own Recent
 * section does (see Rail.vue). AppShell adds no padding to <main>; every page owns its own (see each
 * page's comment for the exact numbers agreed in the shell engineer's report).
 *
 * mail-admin-handoff-v2-changes/CHANGES.md §2 -> "Opening": the Find tenant palette opens from the
 * rail field (Rail.vue) and from ⌘K/Ctrl+K anywhere in the app — there's no shortcut system in this
 * SPA (IMPLEMENTATION_PLAN.md §1), so this is the one global `keydown` listener for it.
 */
import { onBeforeUnmount, onMounted } from 'vue';
import { createTenantOpen, findTenantOpen } from '@/session';
import Rail from './Rail.vue';
import CreateTenantDialog from '@/components/tenants/CreateTenantDialog.vue';
import FindTenantPalette from './FindTenantPalette.vue';
import { AlertTriangle } from '@lucide/vue';

defineProps<{ loadError?: string | null }>();

/** True while typing the shortcut would edit text rather than trigger it — an <input>/<textarea>
 * (or a contenteditable) that already holds content. An empty field doesn't count: there's nothing
 * to lose by hijacking ⌘K/Ctrl+K there. */
function isEditableWithContent(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.value.length > 0;
  }

  return target.isContentEditable && (target.textContent?.length ?? 0) > 0;
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
  if (isEditableWithContent(event.target)) return;

  // Always the browser's own ⌘K/Ctrl+K (a location-bar focus in most browsers) — prevented
  // unconditionally, even if the palette is already open, since we're handling the shortcut either way.
  event.preventDefault();
  // Toggling while already open just keeps it open (CHANGES.md §2) rather than closing it.
  findTenantOpen.value = true;
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKeydown));
</script>

<template>
  <div class="flex h-screen overflow-hidden bg-paper">
    <Rail />

    <main class="min-w-0 grow overflow-y-auto">
      <!-- A non-401 failure to load the session (me/tenants/scopes/event-types) still gets the
           rail and routing chrome — just a quiet line here instead of a blank page. -->
      <div v-if="loadError" class="flex items-center gap-2 px-14 pt-8 type-body text-danger-fg">
        <AlertTriangle :size="16" :stroke-width="1.75" aria-hidden="true" />
        Failed to load: {{ loadError }}
      </div>

      <RouterView />
    </main>

    <CreateTenantDialog :open="createTenantOpen" @close="createTenantOpen = false" />
    <FindTenantPalette />
  </div>
</template>
